/**
 * Maps the user's real Strapi admin permissions (action + subject pairs from
 * GET /admin/users/me/permissions) to the frontend module keys used by the
 * sidebar and ProtectedRoute.
 */
import { getFullAdminUrl } from '../config/api.config';

/** Content-type subject -> frontend module keys it unlocks */
const SUBJECT_MODULES: Record<string, string[]> = {
  'api::dashboard.dashboard': ['dashboard'],
  'api::client.client': ['clients'],
  'api::branch.branch': ['branches'],
  'api::provider.provider': ['providers'],
  'api::category.category': ['categories'],
  'api::code-prefix.code-prefix': ['code-prefixes'],
  'api::employee.employee': ['employees', 'deactivated-employees', 'crew-employee-history'],
  'api::candidate.candidate': ['hiring', 'careers', 'candidates'],
  'api::purchase.purchase': ['purchases'],
  'api::invoice.invoice': ['invoices'],
  'api::invoice-hire.invoice-hire': ['invoices', 'invoice-hire', 'foa'],
  'api::foa.foa': ['foa'],
  'api::bill.bill': ['bills'],
  'api::contract.contract': ['contracts'],
  'api::bank.bank': ['banks'],
  'api::safe.safe': ['safes'],
  'api::petty-cash.petty-cash': ['petty-cash'],
  'api::deduction.deduction': ['deductions'],
  'api::employee-deduction.employee-deduction': ['employee-deductions'],
  'api::debit-note.debit-note': ['debit-notes'],
  'api::payment-order.payment-order': ['payment-order'],
  'api::requisition.requisition': ['requisitions'],
  'api::delivery-note.delivery-note': ['delivery-notes'],
  'api::currency.currency': ['currencies'],
  'api::product.product': ['inventory'],
  'api::transaction.transaction': ['transactions'],
  'api::car.car': ['cars', 'car-coast-guard-permits', 'car-letters'],
  'api::crew-certificate.crew-certificate': ['crew-certificates'],
  'api::crew-change.crew-change': ['crew-change'],
  'api::crew-list.crew-list': ['crew-list', 'crew-list-arabic', 'active-crew-list-arabic'],
  'api::crew-evaluation.crew-evaluation': ['crew-evaluation', 'vessel-report'],
  'api::rank.rank': ['ranks'],
  'api::personnel-permit.personnel-permit': ['personnel-coast-guard-permits'],
  'api::car-permit.car-permit': ['car-coast-guard-permits'],
  'api::letter-generation.letter-generation': ['personnel-letters', 'car-letters', 'raaft-letters', 'taeen-letters'],
  'api::timesheet.timesheet': ['timesheet', 'salary-report'],
  'api::employee-document.employee-document': ['document-settings'],
  'api::document-type.document-type': ['document-settings'],
  'api::erp-setting.erp-setting': ['settings', 'website-settings'],
  'api::finance-report.finance-report': ['finance-reports'],
  'api::location.location': ['locations'],
  'api::assessment-point.assessment-point': ['points'],
  'api::candidate-rating.candidate-rating': ['candidates'],
  'api::permit-letter.permit-letter': ['personnel-letters', 'car-letters', 'raaft-letters', 'taeen-letters'],
  'api::fleet-vessel.fleet-vessel': ['website-settings'],
  'api::website-hero-setting.website-hero-setting': ['website-settings'],
  'api::contact-office.contact-office': ['website-settings'],
  'api::car-document.car-document': ['cars'],
  'api::bank-adjustment.bank-adjustment': ['banks'],
  'api::safe-adjustment.safe-adjustment': ['safes'],
  'api::petty-cash-expense.petty-cash-expense': ['petty-cash'],
  'api::invoice-item.invoice-item': ['invoices'],
  'api::invoice-hire-item.invoice-hire-item': ['invoices', 'invoice-hire', 'foa'],
  'api::payment-order-item.payment-order-item': ['payment-order'],
  'api::purchase-item.purchase-item': ['purchases'],
  'api::requisition-item.requisition-item': ['requisitions'],
  'api::delivery-note-item.delivery-note-item': ['delivery-notes'],
  'api::service-item.service-item': ['invoices', 'invoice-hire'],
};

/** Explorer action -> short action name appended as `<module>:<action>` keys */
const EXPLORER_ACTION_RE = /^plugin::content-manager\.explorer\.(create|read|update|delete|publish)$/;

export function modulesFromAdminPermissions(
  perms: Array<{ action?: string; subject?: string | null }>
): string[] {
  const modules = new Set<string>();
  perms.forEach((p) => {
    if (p.subject && SUBJECT_MODULES[p.subject]) {
      const mods = SUBJECT_MODULES[p.subject];
      mods.forEach((m) => modules.add(m));
      // Action-level pseudo-keys (e.g. 'hiring:delete') so pages can gate
      // per-action UI like delete buttons. `hasPermission` uses exact-match
      // includes(), so these never collide with plain module keys.
      const action = p.action?.match(EXPLORER_ACTION_RE)?.[1];
      if (action) mods.forEach((m) => modules.add(`${m}:${action}`));
    }
    if (p.action?.startsWith('admin::roles.')) modules.add('roles');
    if (p.action?.startsWith('admin::users.')) modules.add('system-users');
  });
  // The general finance reports page aggregates finance data the user can read
  if (['transactions', 'invoices', 'bills', 'banks', 'safes'].some((m) => modules.has(m))) {
    modules.add('finance-reports');
  }
  return Array.from(modules);
}

/** Fetch the current user's own permissions and derive frontend modules */
export async function fetchModulePermissions(token: string): Promise<string[]> {
  const response = await fetch(getFullAdminUrl('/admin/users/me/permissions'), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) throw new Error('Failed to fetch user permissions');
  const data = await response.json();
  const perms = data?.data || data || [];
  return modulesFromAdminPermissions(Array.isArray(perms) ? perms : []);
}
